import { GridColDef } from "@mui/x-data-grid";
import { Chip, Tooltip } from "@mui/material";
import CustomEvaluationHeader from "./CustomEvaluationHeader";
import CustomEvaluationValue from "./CustomEvaluationValue";
import { getFormattedDate } from "../../../utils/helper";
import { SampleFlagTooltip } from "./SampleFlagTooltip";
import LinkField from "../LinkField";

const SEVERITY_ORDER: { [key: string]: number } = {
  high: 3,
  medium: 2,
  low: 1,
  "-": 0,
  "": 0,
};

const severitySortComparator = (v1: string, v2: string) =>
  (SEVERITY_ORDER[(v1 || "").toLowerCase()] ?? 0) -
  (SEVERITY_ORDER[(v2 || "").toLowerCase()] ?? 0);

// AE/PC/SS are auto-reviewed (toggled) when the record was already reported in
// the source system; HC is never toggled.
const isToggled = (row: any) =>
  Boolean(
    row?.autoReviewed?.ae || row?.autoReviewed?.ss || row?.autoReviewed?.pc,
  );

// Same severity colors as the record detail view (RecordDetailPanel), paler tone.
const getSeverityStyle = (severity: string): { bg: string; text: string } => {
  switch ((severity || "").toLowerCase()) {
    case "high":
      return { bg: "#ffcdd2", text: "#b71c1c" };
    case "medium":
      return { bg: "#ffe0b2", text: "#8a3d00" };
    case "low":
      return { bg: "#fff9c4", text: "#6d5200" };
    default:
      return { bg: "#eeeeee", text: "#424242" };
  }
};

const renderRiskChip = (value: string) => {
  if (!value || value === "-") return <span>-</span>;
  if (value === "toggled") {
    return (
      <Chip
        label="Reported"
        size="small"
        sx={{
          backgroundColor: "#e0e0e0",
          color: "#595959",
          textTransform: "capitalize",
        }}
      />
    );
  }
  const { bg, text } = getSeverityStyle(value);
  return (
    <Chip
      label={value}
      size="small"
      sx={{
        backgroundColor: bg,
        color: text,
        textTransform: "capitalize",
      }}
    />
  );
};

export const columns: GridColDef[] = [
  {
    field: "isSampled",
    headerName: "",
    width: 50,
    headerAlign: "center",
    align: "center",
    sortable: false,
    headerClassName: "super-app-theme--header",
    renderCell: (params) =>
      params.value ? (
        <SampleFlagTooltip
          creatorEmail={params?.row?.creatorEmail}
          sampleId={params?.row?.sampleId}
        />
      ) : null,
  },
  {
    field: "sourceId",
    headerName: "Source",
    flex: 1,
    minWidth: 100,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    renderCell: (params) => (
      <LinkField
        url={params.row?.ocepLink}
        label={params.value}
        tooltip={{ title: "Open record in source system" }}
        stopPropagation
      />
    ),
  },
  {
    field: "recordId",
    headerName: "Record ID",
    flex: 1,
    minWidth: 180,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "hcRisk",
    headerName: "HC Risk",
    flex: 1,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    sortComparator: severitySortComparator,
    valueGetter: (_value, row) =>
      row?.risk?.includes("HC") ? row?.hcSeverity || "-" : "-",
    renderCell: (params) => {
      const chip = renderRiskChip(params.value);
      const reason = params.row?.hcReason;
      return reason && params.value !== "-" ? (
        <Tooltip
          title={
            <span>
              <strong>Reason:</strong> {reason}
            </span>
          }
          arrow
        >
          {chip}
        </Tooltip>
      ) : (
        chip
      );
    },
  },
  {
    field: "aePcSsRisk",
    headerName: "AE, PC, SS Risk",
    flex: 1,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    sortComparator: severitySortComparator,
    valueGetter: (_value, row) => {
      if (isToggled(row)) return "toggled";
      return /AE|PC|SS/.test(row?.risk || "")
        ? row?.aePcSsSeverity || "-"
        : "-";
    },
    renderCell: (params) => {
      if (params.value === "toggled") {
        return (
          <Tooltip title="Already reported in safety system" arrow>
            {renderRiskChip("toggled")}
          </Tooltip>
        );
      }
      const chip = renderRiskChip(params.value);
      const reason = params.row?.aePcSsReason;
      return reason && params.value !== "-" ? (
        <Tooltip
          title={
            <span>
              <strong>Reason:</strong> {reason}
            </span>
          }
          arrow
        >
          {chip}
        </Tooltip>
      ) : (
        chip
      );
    },
  },
  {
    field: "evaluation",
    headerName: "Evaluation",
    flex: 2,
    minWidth: 200,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
    renderHeader: () => <CustomEvaluationHeader />,
    renderCell: (params) => <CustomEvaluationValue data={params?.value} />,
    sortable: false,
  },
  {
    field: "reviewType",
    headerName: "Review Type",
    flex: 2,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "extracted",
    headerName: "Extraction Date",
    flex: 2,
    minWidth: 150,
    renderCell: (params) => <span>{getFormattedDate(params.value)}</span>,

    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "region",
    headerName: "Region",
    flex: 1,
    minWidth: 100,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "country",
    headerName: "Country",
    flex: 1,
    minWidth: 100,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
  {
    field: "product",
    headerName: "Product",
    flex: 2,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: "super-app-theme--header",
  },
];

const ADMIN_ROLE = "TextSentinel_profile_group_admin";

const RISK_COLUMN_TEAMS: Record<string, string[]> = {
  hcRisk: ["PCO"],
  aePcSsRisk: ["PV", "PTQ"],
};

export const getVisibleColumns = (
  userInfo: { role?: string; profiles?: string[] } | null,
): GridColDef[] => {
  if (userInfo?.role === ADMIN_ROLE) return columns;
  const teams = userInfo?.profiles || [];
  return columns.filter((col) => {
    const allowedTeams = RISK_COLUMN_TEAMS[col.field];
    return !allowedTeams || allowedTeams.some((team) => teams.includes(team));
  });
};
